import { DownOutlined, LoadingOutlined } from "@ant-design/icons";
import { AddCircleSharp, ArrowDownward, RemoveCircle } from '@mui/icons-material';
import {
  Button,
  Checkbox,
  Dropdown,
  Modal,
  Row,
  Space,
  Spin
} from "antd";
import AnimatedCheckMark from "components/AnimatedCheckMark";
import Loading from "components/Loading";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useWorkSheetImageSelector } from "store/worksheet/selectors";
import styled from "styled-components";
import ClickOutside from "components/OnClickOutside";
import {
  question_type_generate_more_worksheet
} from "utils/config/reference";
import { handleTypeText, lengthOptions } from "../helper";
import { QuizCardSkeleton } from "components/UI/Skeltonloader/WorksheetSkelton";
import SentryWrap from 'components/Sentry';


const QuestionContainer = styled(Row)`
  margin-top: 20px;
  margin-bottom: 10px;
  gap: 20px;
`;
const QuestionArea = styled.div`
  width: 48%;
  margin-bottom: 10px;
  @media Screen and (max-width: 769px) {
    width: 98%;
  }
`;
const MainContainer = styled.div`
  height: 68vh;
  overflow-y: auto;
`;

function shuffleOptions(question) {
  if (!question.options) return [];
  const shuffled = [...question.options].sort(() => Math.random() - 0.5);
  return shuffled;
}

function shuffleOptionsDisplay(question) {
  const shuffled = [...question.options].sort(() => Math.random() - 0.5);
  question.shuffledOptions = shuffled;
  return shuffled;
}

const SelectQaOption = ({
  loading,
  questionData,
  openModal,
  onCancel,
  handleGenerateMoreQnA = () => {},
  selectedTypes,
  setSelectedTypes,
  handleSaveWorksheet,
  questionType = [],
  questionLengthValue = "",
  questionCount
}) => {
  const containerRef = useRef();
  const prevGeneratedCountRef = useRef(questionCount);
  const workSheetImage = useWorkSheetImageSelector();
  const [open, setOpen] = useState(false);
  const [loader, setLoader] = useState(false);
  const [selected, setSelected] = useState([]);
  const [showJumpButton, setShowJumpButton] = useState(false);
  const [newQuestionImage, setNewQuestionImage] = useState([]);
  const [openNumberQuestion, setOpenNumberQuestion] = useState(false);
  const [questionLength, setQuestionLength] = useState(questionLengthValue);

  const checkIfAtBottom = useCallback(() => {
    const container = containerRef.current;
    if (container) {
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      const isAtBottom = scrollHeight - scrollTop - clientHeight <= 20;
      return isAtBottom;
    }
    return false;
  }, []);

  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (container) {
      const hasScrollableContent = container.scrollHeight > container.clientHeight;
      if (hasScrollableContent) {
        const isAtBottom = checkIfAtBottom();
        setShowJumpButton(!isAtBottom);
      } else {
        setShowJumpButton(false);
      }
    }
  }, [checkIfAtBottom]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      handleScroll();
      container.addEventListener('scroll', handleScroll);
      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, [questionData, openModal, handleScroll]);

  useEffect(() => {
    if (questionCount > prevGeneratedCountRef.current) {
      const container = containerRef.current;
      if (container && container.scrollHeight - container.scrollTop > container.clientHeight + 300) {
        setShowJumpButton(true);
      }
    }
    prevGeneratedCountRef.current = questionCount;
  }, [questionCount]);

  useEffect(() => {
    if (workSheetImage && questionData?.length > 0) {
      setNewQuestionImage((prev) => [...prev, workSheetImage]);
    }
  }, [workSheetImage]);

  useEffect(() => {
    if (openModal && question_type_generate_more_worksheet) {
      const allValues = question_type_generate_more_worksheet
        .filter((option, index) =>
          questionType?.includes((index + 1)?.toString())
        )
        .map((option) => option.value);

      setSelectedTypes(allValues);
    }
  }, [
    openModal,
    setSelectedTypes,
    questionType,
    question_type_generate_more_worksheet,
  ]);

  const processQuestion = (question) => {
    const cleanQuotes = (value) => {
      if (typeof value === "string") {
        return value.replace(/^"(.*)"$/, "$1");
      }
      return value;
    };
    const processed = {
      ...question,
      tense: cleanQuotes(question?.tense),
      mood: cleanQuotes(question?.mood),
      category: cleanQuotes(question?.category),
      pronouns: cleanQuotes(question?.pronouns),
      options: question.shuffledOptions || shuffleOptions(question),
    };
  
    processed.question = processed.question.replace(/<[^>]*>/g, "");
  
    if (processed.type === "Fill_IN_THE_Blank") {
      const highlightArray = Array.isArray(processed.highlightWords) ? processed.highlightWords : [];
      const correctedHighlights = highlightArray.map((h) => {
        const actualText = processed.question.slice(h.start, h.end);
        if (actualText === h.text) return h;
  
        const idx = processed.question.indexOf(h.text);
        if (idx !== -1) {
          return {
            ...h,
            start: idx,
            end: idx + h.text.length,
          };
        }
  
        return h;
      });
  
      processed.highlightWords = correctedHighlights;
    }
  
    return processed;
  };
  

  const handleButtonClick = (id) => {
    const selectedQuestion = questionData.find((q) => q.id === id);
    const questionWithShuffledOptions = processQuestion(selectedQuestion);
  
    setSelected((prevSelected) => {
      if (!prevSelected) return [questionWithShuffledOptions];
  
      const index = prevSelected.findIndex((q) => q.id === id);
      if (index === -1) {
        return [...prevSelected, questionWithShuffledOptions];
      } else {
        const newSelected = [...prevSelected];
        newSelected.splice(index, 1);
        return newSelected;
      }
    });
  };  

  const handleCheckboxChange = (value, e) => {
    e.stopPropagation();
    setSelectedTypes((prev) => {
      if (prev.includes(value)) {
        return prev.filter((type) => type !== value);
      }
      return [...prev, value];
    });
  };

  const handleCheckboxChangeForTypeChange = (option) => {
    setQuestionLength(option?.label);
    setOpenNumberQuestion(false);
  };

  const selected_length = selected?.length || 0;

  const questionLengthMenu = {
    items: lengthOptions?.map((option, index) => ({
      key: index,
      label: (
        <div onClick={(e) => handleCheckboxChangeForTypeChange(option)}>
          {option.label}
        </div>
      ),
    })),
  };

  const menu = {
    items: question_type_generate_more_worksheet?.map((option, index) => ({
      key: index,
      label: (
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={selectedTypes.includes(option.value)}
            onChange={(e) => handleCheckboxChange(option.value, e)}
          >
            {option.label}
          </Checkbox>
        </div>
      ),
    })),
  };

  const handleCloseModal = () => {
    setNewQuestionImage([]);
    setSelected([]);
    onCancel();
  };


  useEffect(() => {
    setQuestionLength(questionLengthValue);
  }, [questionLengthValue]);


  const handleSelectAll = () => {
    if (selected?.length === questionData?.length) {
      setSelected([]);
      return;
    }
    const processedAll = questionData.map((q) => processQuestion(q));
    setSelected(processedAll);
  };  

  const cleanString = (str) => String(str ?? "").replace(/^"(.*)"$/, "$1").replace(/<[^>]*>/g, "");

  return (
    <SentryWrap>
      <Modal
        open={openModal}
      onCancel={handleCloseModal}
      className={`scenMdlWrapCstm scenarioMdlWrapNew gmScenarioSecondAI`}
      maskClosable={false}
    >
      <div className="writeTpcUpr">
        <div className="createBckBtn text-dark">AI Creator</div>

        {/* <div className="writeTpcEbk writeTpcEbkSection">
          <p>Your questions are ready for your selection! </p>
          <img
            src={
              "https://firebasestorage.googleapis.com/v0/b/yo-americano.appspot.com/o/AI_Topic.png?alt=media&token=e1a09441-0c55-4be0-a545-d0a579f8d7222"
            }
            alt=""
          />
        </div> */}
        <div className="generateMore">
          <p
            style={{
              backgroundColor: "transparent",
              paddingInline: "18px",
              paddingTop: "10px",
              fontSize: "20px",
            }}
          >
            Your questions are ready for your selection!{" "}
          </p>
          <div className="subGenerateMore justify-end">
            <div className="flex items-center gap-10px flex-wrap">
              <Button
                type="link"
                size="small"
                onClick={handleSelectAll}
              >
                {selected?.length === questionData?.length ?
                 (
                  <div style={{gap: "5px"}} className="flex items-center">
                   <RemoveCircle /> Deselect All Question
                  </div>
                 ): (
                  <div style={{gap: "5px"}} className="flex items-center">
                  <AddCircleSharp /> Select All Question
                  </div>
                 )}
              </Button>

              <Dropdown
                menu={menu}
                trigger={["click"]}
                open={open}
                onOpenChange={(visible) => {
                  setOpen(visible);
                }}
                popupRender={(menu) => (
                  <ClickOutside onClickOutside={() => setOpen(false)}>
                    <QnaLoaderQuestionType 
                      className="qna-loader-question-type"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Space direction="vertical">
                        {menu}
                        <div
                          style={{
                            borderTop: "1px solid #f0f0f0",
                            padding: "8px 0 0",
                            paddingBottom:"8px"
                          }}
                        >
                          <Button
                            type="link"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTypes(
                                question_type_generate_more_worksheet.map(
                                  (opt) => opt.value
                                )
                              );
                            }}
                          >
                            Select All
                          </Button>
                          <Button
                            type="link"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTypes([]);
                            }}
                          >
                            Clear All
                          </Button>
                        </div>
                      </Space>
                    </QnaLoaderQuestionType>
                  </ClickOutside>
                )}
              >
                <Button>
                  Question Types ({selectedTypes?.length}) <DownOutlined />
                </Button>
              </Dropdown>

              <Dropdown
                menu={questionLengthMenu}
                trigger={["click"]}
                open={openNumberQuestion}
                onOpenChange={setOpenNumberQuestion}
                popupRender={(menu) => (
                  <QnaLoaderQuestionType className="qna-loader-question-type">
                    <Space direction="vertical">{menu}</Space>
                  </QnaLoaderQuestionType>
                )}
              >
                <Button style={{ marginLeft: 10 }}>
                  Number of Questions ({questionLength}) <DownOutlined />
                </Button>
              </Dropdown>

              <Button
                className="gmAI"
                style={{ marginLeft: 10 }}
                onClick={async () => {
                  handleGenerateMoreQnA(questionLength);
                }}
                disabled={questionCount > 0 ? true : selectedTypes?.length === 0}
              >
                Generate more
                {loading?.type === "generateMore" && loading?.value === true ? (
                  <Spin
                    indicator={
                      <LoadingOutlined
                        style={{
                          fontSize: 24,
                          color: "white",
                          marginLeft: "10px",
                        }}
                        spin
                      />
                    }
                  />
                ) : (
                  ""
                )}
              </Button>
            </div>
          </div>
        </div>
        <MainContainer className="quesSelect" ref={containerRef}>
          <QuestionContainer>
            {Array.isArray(questionData) &&
              questionData.map((question, index) => {
                const id = question.id || index;
                const is_selected = selected?.some((item) => item.id === id);
                // Match the worksheet image with the question ID
                return (
                  <QuestionArea key={id} className="quesSelectTxt">
                    <div className="questionContainer">
                      <Button
                        onClick={(e) => {
                          handleButtonClick(question.id);
                        }}
                        className={
                          is_selected ? "greenButton" : "defaultButton"
                        }
                        disabled={
                          loader?.type === "generateMore" &&
                          loader?.value === true
                        }
                      >
                        {is_selected ? "Selected" : "Select"}
                        {is_selected ? (
                          <RemoveCircle />
                        ) : (
                          <AddCircleSharp />
                        )}
                      </Button>
                      <p
                        style={{
                          position: "absolute",
                          top: -14,
                          padding: "0px 10px",
                          background: "white",
                          borderRadius: 25,
                          color: "black",
                          fontSize: 15,
                        }}
                      >
                        {questionTypeMap[question?.type] || ""}
                      </p>
                      <p
                        className="text-center"
                        style={{ marginTop: 5, fontSize: 12 }}
                      >
                        {handleTypeText(question?.type)}
                      </p>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div style={{ display: "flex", marginTop: 5 }}>
                          <p style={{ fontSize: 12 }}>Mood -</p>
                          <p style={{ marginLeft: 10, fontSize: 12 }}>
                            {cleanString(question?.mood)}
                          </p>
                        </div>
                        <div style={{ display: "flex", marginTop: 5 }}>
                          <p style={{ fontSize: 12 }}>Tense -</p>
                          <p style={{ marginLeft: 10, fontSize: 12 }}>
                              {cleanString(question?.mood) === "Imperative" ? "" : cleanString(question?.tense)}
                          </p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div style={{ display: "flex", marginTop: 5 }}>
                          <p style={{ fontSize: 12 }}>Category -</p>
                          <p style={{ marginLeft: 10, fontSize: 12 }}>
                            {cleanString(question?.category)}
                          </p>
                        </div>
                        <div style={{ display: "flex", marginTop: 5 }}>
                          <p style={{ fontSize: 12 }}>Pronouns -</p>
                          <p style={{ marginLeft: 10, fontSize: 12 }}>
                            {cleanString(question?.pronouns)}
                          </p>
                        </div>
                      </div>
                      <p>
                        {index + 1}.{" "}
                        {question.question?.replace(/<[^>]*>/g, "")}
                      </p>
                      {question.type === "Fill_IN_THE_Blank" ||
                        (question?.type === "Fill_in_the_Blank" && (
                          <div>
                            <p className="optionsContainerCheck 11">
                              <label>Correct Answer: &nbsp;</label>
                              {question?.correct_answer}
                              <AnimatedCheckMark
                                style={{ marginLeft: "5px" }}
                              />
                            </p>
                          </div>
                        ))}
                      {question.type === "Translate_Text" && (
                        <div className="optionsContainerCheck optionsContainerCheckText">
                          {(
                            question.shuffledOptions ||
                            shuffleOptionsDisplay(question)
                          ).map((option, optionIndex) => (
                            <p
                              key={optionIndex}
                              className="optionsContainerCheck 22"
                            >
                              {String.fromCharCode(65 + optionIndex)}. {option}
                              {(question?.correct_answer === option ||
                                question?.answerText === option) && (
                                <AnimatedCheckMark
                                  style={{ marginLeft: "5px" }}
                                />
                              )}
                            </p>
                          ))}
                        </div>
                      )}
                      {question.type === "Select_Correct_Response" && (
                        <div className="optionsContainerCheck optionsContainerCheckText">
                          {(
                            question.shuffledOptions ||
                            shuffleOptionsDisplay(question)
                          ).map((option, optionIndex) => (
                            <p
                              key={optionIndex}
                              className="optionsContainerCheck 33"
                            >
                              {String.fromCharCode(65 + optionIndex)}. {option}
                              {(question?.correct_answer === option ||
                                question?.answerText === option) && (
                                <AnimatedCheckMark
                                  style={{ marginLeft: "5px" }}
                                />
                              )}
                            </p>
                          ))}
                        </div>
                      )}
                      {question.type === "Select_What_You_Hear" && (
                        <div className="optionsContainerCheck optionsContainerCheckText">
                          {(
                            question.shuffledOptions ||
                            shuffleOptionsDisplay(question)
                          ).map((option, optionIndex) => (
                            <p
                              key={optionIndex}
                              className="optionsContainerCheck 44"
                            >
                              {String.fromCharCode(65 + optionIndex)}. {option}
                              {(question?.correct_answer === option ||
                                question?.answerText === option) && (
                                <AnimatedCheckMark
                                  style={{ marginLeft: "5px" }}
                                />
                              )}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  </QuestionArea>
                );
              })}
            {questionCount > 0 ? <QuizCardSkeleton /> : null}
          </QuestionContainer>
        </MainContainer>
        <div className="allCreateBtn createSelectedTxt">
          {selected_length > 0 && (
            <div
              className="createBckBtn text-dark"
              style={{ marginRight: "5px" }}
            >
              {`Selected ${selected_length}`}
            </div>
          )}
          {showJumpButton && (
            <Button
              className={'greenButton'}
              style={{
                  display: 'flex',
                  justifyContent: 'center'
              }}
              onClick={() => {
                const container = containerRef.current;
                if (container) {
                  container.scrollTo({
                    top: container.scrollHeight,
                    behavior: 'smooth',
                  });
                  setTimeout(() => {
                    handleScroll();
                  }, 500);
                }
              }}
            >
              {`See More`} <ArrowDownward fontSize='19px' />
            </Button>
          )}
          {questionData?.length > 0 && (
            <Button
              onClick={async (e) => {
                setLoader(true);
                await handleSaveWorksheet(selected);
                setSelected([]);
                setLoader(false);
              }}
              disabled={selected_length === 0}
              className={"text-blue-002d74 "}
            >
              <div className="flex items-center gap-10px w-full justify-center">
                {loader && <Loading size={18} white={true} />}
                <div>Save</div>
              </div>
            </Button>
          )}
        </div>
      </div>
    </Modal>
    </SentryWrap>
  );
};

export default SelectQaOption;

const questionTypeMap = {
  multipleChoice: "Multiple Choice",
  trueFalse: "True or False",
  essay: "Written Response",
  filling: "Fill in the Blanks",
};

const QnaLoaderQuestionType = styled.div`
  background-color: white;
  padding: 8;
  border-radius: 6px;
`;
